import React from 'react';
import PropTypes from 'prop-types';
import moment from "moment";
import classnames from "classnames";

import {FaLock} from "react-icons/fa";

import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";

const ScheduleBuilderOptionsList = function (props) {
    let component;

    // Get only the registered events that can be combined with each individual schedule option
    const registeredEvents = props.eventSources.filter(eventObject => eventObject.hasOwnProperty("registered") && eventObject.registered);
    // Get a list of just the schedule options that are being displayed
    let scheduleOptionEvents = props.eventSources.filter(eventObject => eventObject.hasOwnProperty("isScheduleOption") && eventObject.isScheduleOption);

    let displayedOptions;
    if (registeredEvents.length > 0 && scheduleOptionEvents.length === 0) {
        displayedOptions = registeredEvents;
    } else {
        displayedOptions = scheduleOptionEvents;
    }

    // Build a list of schedule option lists to display
    let displayedOptionsList = displayedOptions.map((eventObject, i) => {
        let elementId = eventObject.name.toLowerCase().replace(/\s/g, "-").trim();

        // Combine the schedule options events with the registered if there are any registered
        let eventSources = [eventObject];
        if (registeredEvents.length > 0 && scheduleOptionEvents.length > 0) {
            eventSources.push(registeredEvents[0]);
        }

        let scheduleListCalendar = [];

        // An array of the days of the week
        let daysOfWeek = moment.weekdays();
        // Create an object with each day of the week being a key and the value being an empty array, Also create a TBA key with an empty array
        let daysObject = daysOfWeek.reduce((acc, i) => (acc[i]=[], acc), {});
        daysObject["TBA"] = [];

        // Iterate the schedule option and registered
        eventSources.map(eventObject => {
            // Iterate the events list
            eventObject.events.map(event => {
                // if the event is all day, its a TBA, push the event data into object for TBA
                if (event.allDay) {
                    daysObject.TBA.push(event);
                } else {
                    // Non-TBA events, iterate of the days of the week the event is scheduled, to push event data into corresponding object key for that day of the week
                    event.daysOfWeek.map(dayIndex => {
                        let dayName = daysOfWeek[dayIndex];
                        daysObject[dayName].push(event);
                    });
                }
            });
        });

        // Iterate over data object to create a list for each day of the week and the scheduled events for each day
        for (const [day, events] of Object.entries(daysObject)) {
            // If the day of the week is empty, skip it, no display
            if (events.length > 0) {
                // If events are for a day of the week, sort events for each day by start time
                if (day !== "TBA") {
                    events.sort((a, b) => {
                        if (a.startTime < b.startTime) return -1;
                        if (a.startTime > b.startTime) return 1;
                        return 0;
                    })
                }

                // Build list items for each event
                let daysEvents = events.map((event, i) => {
                    let time;
                    // Build time element for scheduled events
                    if (day !== "TBA") {
                        time =
                            <span className="d-inline-block ms-auto">
                                <time dateTime={event.startTime}>{moment(event.startTime, "HH:mm").format("h:mm A")}</time>
                                <span aria-hidden={true}> - </span><span className="sr-only">to</span>
                                <time dateTime={event.endTime}>{moment(event.endTime, "HH:mm").format("h:mm A")}</time>
                            </span>;
                    }

                    let eventBadges;
                    if (event.hasOwnProperty("extendedProps") && event.extendedProps.hasOwnProperty("badges") && event.extendedProps.badges.length > 0) {
                        eventBadges = event.extendedProps.badges.map((badge, i) => {return(
                            <Badge key={i} pill bg="light-gray" className="text-dark align-middle fs-10 text-uppercase fw-normal border ms-2">
                                {badge}
                            </Badge>
                        )});
                    }

                    let registered;
                    if (event.hasOwnProperty("extendedProps") && event.extendedProps.hasOwnProperty("registered") && event.extendedProps.registered) {
                        registered = <small><FaLock aria-hidden="true" className="text-muted ms-2"  title="Registered"/></small>;
                    }

                    return (
                        <li key={i} className="d-flex">
                            <button className="border-0 bg-transparent w-100 px-3 py-2 text-start d-flex align-items-center"
                                    onClick={() => props.eventClick({event})} type="button">
                                <div className="d-inline-block">
                                    <span className="align-middle">{event.title}</span>
                                    {eventBadges}
                                    {registered}
                                </div>
                                {time}
                            </button>
                        </li>
                    );
                });

                let heading;
                if (day !== "TBA") {
                    heading =
                        <>
                            <abbr title={day} aria-hidden={true} className="d-none d-lg-inline fs-4 mb-0">{moment(day, "dddd").format("ddd")}</abbr>
                            <span className="d-inline d-lg-none">{day}</span>
                        </>;
                } else {
                    heading =
                        <>
                            <abbr title="To be announced" aria-hidden={true} className="d-none d-lg-inline fs-4 mb-0">{day}</abbr>
                            <abbr title="To be announced" aria-hidden={true} className="d-inline d-lg-none">{day}</abbr>
                        </>;
                }

                // Add day with list of events to the over list for schedule option
                scheduleListCalendar.push(
                    <li key={scheduleListCalendar.length + 1} className="p-0">
                        <Row className="g-0">
                            <Col id={`${elementId}-${day.toLowerCase()}`}
                                 as="h5"
                                 xs={12} lg={3}
                                 className="text-muted px-3 pt-2 pb-lg-1 pb-2 mb-0 text-nowrap">
                                <span className="sr-only">{day !== "TBA" ? day : 'To be announced'}</span>
                                {heading}
                            </Col>
                            <Col as="ul"
                                 className="list-unstyled"
                                 aria-labelledby={`${elementId}-${day.toLowerCase()}`}>
                                {daysEvents}
                            </Col>
                        </Row>
                    </li>
                );
            }
        }

        // Return a schedule option list, a list of days containing a list of events
        return (
            <Col key={i} className="min-w-100 mw-100 min-w-sm-50 mw-sm-50 min-w-md-33 mw-md-50">
                <Card className={classnames(eventObject.classNames[0])}>
                    <Card.Header>
                        <Card.Title as="h4" className="mb-0" id={elementId}>{eventObject.name}</Card.Title>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <ul className="list-unstyled mb-0" aria-labelledby={elementId}>
                            {scheduleListCalendar}
                        </ul>
                    </Card.Body>
                </Card>
            </Col>
        );
    });

    component =
        <Row className="scheduleBuilder-list md-gutter" aria-live="polite" aria-atomic="true">
            {displayedOptionsList}
        </Row>


    return component;
}

ScheduleBuilderOptionsList.propTypes = {
    eventSources: PropTypes.array.isRequired, // list of calendar events being displayed,
}

export default ScheduleBuilderOptionsList;